import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPublishedPosts } from '../lib/db';
import type { Post, PostCategory } from '../lib/types';
import { Search, Loader2 } from 'lucide-react';
import { useSeo } from '../lib/seo';

const categories: { value: PostCategory | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'news', label: 'News' },
  { value: 'programs', label: 'Programs' },
  { value: 'impact', label: 'Impact' },
  { value: 'events', label: 'Events' },
];

export default function Programs() {
  useSeo({
    title: 'Programs & News | Kisapuk Community Initiative',
    description: 'Explore Kisapuk Community Initiative’s programs, news, impact stories, and how we support community resilience in Kenya.',
    pathname: '/programs',
    image: 'https://your-domain.com/programs-social.png',
  });

  const [posts, setPosts] = useState<Post[]>([]);
  const [filtered, setFiltered] = useState<Post[]>([]);
  const [category, setCategory] = useState<PostCategory | 'all'>('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPublishedPosts()
      .then((data) => {
        setPosts(data);
        setFiltered(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    let result = posts;
    if (category !== 'all') {
      result = result.filter((p) => p.category === category);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (p) => p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q)
      );
    }
    setFiltered(result);
  }, [category, search, posts]);

  return (
    <div>
      {/* Hero */}
      <section className="pt-32 pb-16 bg-brand-900">
        <div className="container-prose text-center">
          <span className="badge-gold mb-4">Our Work</span>
          <h1 className="font-display text-4xl font-bold text-white sm:text-5xl">
            Programs &amp; News
          </h1>
          <p className="mt-4 text-lg text-brand-100 max-w-2xl mx-auto">
            Discover our initiatives, latest updates, and the impact we're making together.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 md:top-20 z-30 bg-stone-50/95 backdrop-blur-md border-b border-stone-200 py-4">
        <div className="container-prose flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setCategory(cat.value)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  category === cat.value
                    ? 'bg-brand-600 text-white'
                    : 'bg-white text-stone-600 border border-stone-200 hover:border-brand-300'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
              aria-label="Search posts"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-12 lg:py-16">
        <div className="container-prose">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-stone-500 text-lg">No posts found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((post) => (
                <Link
                  key={post.id}
                  to={`/programs/${post.slug}`}
                  className="card overflow-hidden group hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative h-52 overflow-hidden bg-stone-200">
                    {post.cover_image_url && (
                      <img
                        src={post.cover_image_url}
                        alt={post.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    )}
                    <span className="absolute top-3 left-3 badge-gold capitalize">{post.category}</span>
                  </div>
                  <div className="p-6">
                    <p className="text-xs text-stone-500 mb-2">
                      {new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                    <h3 className="font-display text-lg font-semibold text-stone-900 group-hover:text-brand-700 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="mt-2 text-sm text-stone-600 line-clamp-3">{post.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
